/* eslint-disable jsx-a11y/anchor-is-valid */
import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
} from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  FormControl,
  FormHelperText,
  Typography,
  ListItemText,
  ListItem,
  Avatar,
  TextField,
  Box,
  ListItemButton,
  Icon,
  Link,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { find } from 'lodash';

import { getStatuses } from '@pingum/app-state-management/status/actions';
import {
  CheckBox,
  Delete,
} from '@mui/icons-material';
import styles from './styles';
import {
  grey,
  primary,
} from '../../utils/brandColors';

import StatusBadge from '../StatusBadge';
import UserMentionTextField from '../UserMentionTextField';
import BarnUsersAutocomplete from '../Autocompletes/BarnUsers';

// utils
import {
  getColorForItem,
  getIconForItem,
} from '../../utils/iconHelper';
import { getBarnUsersForAssignmentString } from '../../utils/scheduleHelper';

const noChangeStatus = {
  id: -1,
  name: 'Comment',
  iconColor: grey[300],
  icon: 'comment',
  requireComment: false,
};

const useStyles = makeStyles(styles);

export const getStatusItemSecondaryText = (status, eventStatus) => {
  if ((eventStatus && eventStatus.status) && status.id === eventStatus.status.id) {
    return 'Current task status';
  }
  if (status.endingStatus && status.archiveStatus) {
    return 'Completes and Deletes the task';
  }
  if (status.endingStatus) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      ><CheckBox
        color="primary"
        sx={{
          fontSize: 16,
          mr: 0.5,
        }}
      /><Box>Completes Task</Box>
      </Box>
    )
  }
  if (status.archiveStatus) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      ><Delete
        color="secodary"
        sx={{
          fontSize: 16,
          mr: 0.5,
        }}
      /><Box>Deletes Task</Box>
      </Box>
    )
  }
  return '';
};

export const StatusAssignmentComponent = ({
  allowNoStatusChange,
  showCurrentStatusBanner,
  showStatusCommentField,
  selectedStatus,
  event,
  comment,
  eventStatus,
  isValidStatus,
  isValidAssignment,
  assignment,
  handleSelectedStatusChange,
  handleWorkflowStatusChange,
  handleCommentChange,
  handleAssignmentChange,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);
  const isFetchingEventTemplate = useSelector((state) => state.eventTemplate.isFetchingEventTemplate);
  const allEventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);
  const statuses = useSelector((state) => state.status.statuses);

  const [nextWorkflowStatus, setNextWorkflowStatus] = useState(null);
  const [eventTemplate, setEventTemplate] = useState({});
  const [changeAssignment, setChangeAssignment] = useState(false)

  useEffect(() => {
    // load statuses if not already loaded
    if (!statuses || statuses.length === 0) {
      dispatch(getStatuses(false));
    }
  }, []);

  useEffect(() => {
    if ((event && event.eventTemplateId) && (!eventTemplate.id || eventTemplate.id !== event.eventTemplateId)) {
      setEventTemplate(allEventTemplates.find((et) => et.id === event.eventTemplateId) || {})
    } else if (eventTemplate.id) {
      setEventTemplate({})
    }
  }, [event.eventTemplateId]);

  useEffect(() => {
    // let's set the next workflow status based on status selection
    if (
      selectedStatus
        && selectedStatus.id
        && eventTemplate
        && eventTemplate.id
        && eventTemplate.workflowStatuses
    ) {
      const selectedWorkflowStatus = eventTemplate.workflowStatuses.find((workflowStatus) => workflowStatus.statusId === selectedStatus.id);
      setNextWorkflowStatus(selectedWorkflowStatus || null);
      // trigger workflow status change to parent - used in validation of parent
      handleWorkflowStatusChange(selectedWorkflowStatus || null);
    } else {
      setNextWorkflowStatus(null);
      handleWorkflowStatusChange(null);
    }
    setChangeAssignment(false)
  }, [eventTemplate, selectedStatus]);

  useEffect(() => {
    if (event && event.currentEventStatus && statuses) {
    // If the status is not complete, autoselect the complete status on barn
      // if (!event.completeDate && !event.archived) {
      //   const nextStatus = statuses.find((s) => s.id === selectedBarn.barnMetadata.statusEndId);
      //   if (nextStatus) {
      //     handleSelectedStatusChange(nextStatus);
      //   } else {
      //     handleSelectedStatusChange(noChangeStatus);
      //   }
      // } else {
      // // else, default to comment
      //   handleSelectedStatusChange(noChangeStatus);
      // }
    }
  }, [statuses, event]);

  return (
    <div className={classes.container}>
      {
        showCurrentStatusBanner
          && (
            <ListItem className={classes.eventListItem}>
              <Avatar>
                <StatusBadge
                  icon={
                    getIconForItem(statuses, (eventStatus && eventStatus.status)
                      ? eventStatus.status.id
                      : '')
                  }
                  iconColor={
                    getColorForItem(statuses, (eventStatus && eventStatus.status)
                      ? eventStatus.status.id
                      : '')
                  }
                />
              </Avatar>
              <ListItemText
                primary={event.name}
                secondary={
                  (
                    eventStatus
                      && eventStatus.status
                  )
                    ? eventStatus.status.name
                    : ''
                }
              />
            </ListItem>
          )
      }
      <FormControl
        fullWidth
        variant="outlined"
      >
        <TextField
          select
          label="Status"
          name="status"
          id="status-select"
          variant="outlined"
          size="small"
          fullWidth
          error={!isValidStatus && selectedStatus && selectedStatus.id < 0}
          disabled={isFetchingEventTemplate}
          value={(selectedStatus && selectedStatus.id) || null}
          onChange={(e) => {
            const newSelectedStatus = find(statuses, (allowedStatus) => allowedStatus.id === e.target.value) || noChangeStatus;
            handleSelectedStatusChange(newSelectedStatus, nextWorkflowStatus);
          }}
          SelectProps={{
            renderValue: () => (
              <ListItem
                dense
                value={selectedStatus && selectedStatus.id}
              >
                <Icon
                  sx={{
                    mr: 1,
                    color: (selectedStatus && selectedStatus.id) < 0
                      ? grey[400]
                      : primary[500],
                  }}
                >{(selectedStatus && selectedStatus.icon) || 'question'}
                </Icon>
                <ListItemText
                  primary={(selectedStatus && selectedStatus.name) || 'Select'}
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={getStatusItemSecondaryText(selectedStatus, {})}
                />
              </ListItem>
            ),
            classes: { select: classes.input },
          }}
        >
          <ListItemButton
            value={null}
            dense
            divider
          >
            <ListItemText primary="Select an action to take" />
          </ListItemButton>
          {
            allowNoStatusChange && (
              <ListItemButton
                key="status-no-change"
                value={noChangeStatus.id}
                dense
                divider
              >
                <Icon
                  sx={{
                    mr: 1,
                    color: grey[400],
                  }}
                >{noChangeStatus.icon}
                </Icon>
                <ListItemText primary={noChangeStatus.name} secondary="Leave a comment on this task. Do not change status." />
              </ListItemButton>
            )
          }
          {
            statuses.sort((a, b) => a.statusOrder - b.statusOrder).map((status) => (
              <ListItemButton
                dense
                key={`status-${status.id}`}
                value={status.id}
                button={!eventStatus || !(eventStatus && eventStatus.status)
                  || ((eventStatus && eventStatus.status) && eventStatus.status.id !== status.id)}
                disabled={eventStatus && (eventStatus && eventStatus.status)
                  && eventStatus.status.id === status.id}
              >
                <Icon
                  sx={{
                    mr: 1,
                    color: status.id < 0
                      ? grey[400]
                      : primary[500],
                  }}
                >{status.icon}
                </Icon>
                <ListItemText
                  primary={status.name}
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={getStatusItemSecondaryText(status, eventStatus)}
                />
              </ListItemButton>
            ))
          }
        </TextField>
        {
          (selectedStatus && selectedStatus.id) < 0
            && (
              <FormHelperText>
                <Typography variant="caption" gutterBottom>
                  No status selected, this will leave only a comment.
                </Typography>
              </FormHelperText>
            )
        }
        {
          (selectedStatus && selectedStatus.endingStatus)
            && (
              <FormHelperText>
                <Typography variant="caption" gutterBottom>
                  Selecting this status will mark the task <b>complete</b>.
                </Typography>
              </FormHelperText>
            )
        }
      </FormControl>
      {
        (selectedStatus && selectedStatus.id) >= 0 && (
          <Box sx={{ mt: 2 }}>
            {
              changeAssignment
                ? (
                  <BarnUsersAutocomplete
                    label="Assignment"
                    id="barn-users-task-status-autocomplete-assignment"
                    multiple
                    size="small"
                    validation={!isValidAssignment
                      ? 'Assignment required at this status'
                      : undefined}
                    required={nextWorkflowStatus && nextWorkflowStatus.requiredAssignment}
                    value={getBarnUsersForAssignmentString(assignment || '', barnUsersMap)}
                    onSelect={(e, newBarnUsers) => {
                      // take selected barn users and map to assignment syntax @[NAME](AUTH0_USER_ID)
                      handleAssignmentChange(newBarnUsers.map((bu) => (bu.id
                        ? `@[${bu.userFullName}](${bu.auth0UserId || bu.email})`
                        : `@[${bu.userFullName || bu}](${null})`)).join(''), nextWorkflowStatus);
                    }}
                    limitTags={5}
                  />
                )
                : (
                  <Link
                    component="button"
                    variant="body2"
                    color="secondary"
                    underline="hover"
                    onClick={() => {
                      setChangeAssignment(true)
                    }}
                  >
                    Change Assigned User
                  </Link>
                )
            }
          </Box>
        )
      }
      {
        showStatusCommentField && (
          <div className={classes.commentFormContainer}>
            <UserMentionTextField
              disabled={isFetchingEventTemplate}
              value={comment}
              label="Comment"
              onChange={handleCommentChange}
              required={selectedStatus && selectedStatus.requireComment}
              error={!isValidStatus}
              helperText={!isValidStatus
                ? 'A comment is required for this status change'
                : 'Mention people using @ + name/email'}
            />
          </div>
        )
      }
    </div>
  );
};

StatusAssignmentComponent.propTypes = {
  // configs
  allowNoStatusChange: PropTypes.bool,
  showCurrentStatusBanner: PropTypes.bool,
  showStatusCommentField: PropTypes.bool,
  isValidStatus: PropTypes.bool,
  isValidAssignment: PropTypes.bool,
  // data
  comment: PropTypes.string,
  eventStatus: PropTypes.object,
  selectedStatus: PropTypes.object,
  assignment: PropTypes.string,
  event: PropTypes.object,
  // callbacks
  handleSelectedStatusChange: PropTypes.func.isRequired,
  handleWorkflowStatusChange: PropTypes.func.isRequired,
  handleAssignmentChange: PropTypes.func.isRequired,
  handleCommentChange: PropTypes.func,
};

StatusAssignmentComponent.defaultProps = {
  allowNoStatusChange: false,
  showCurrentStatusBanner: false,
  showStatusCommentField: false,
  comment: '',
  selectedStatus: noChangeStatus,
  assignment: null,
  event: {},
  eventStatus: {},
  isValidStatus: true,
  isValidAssignment: true,
  handleCommentChange: () => {},
};

export default StatusAssignmentComponent;
